// The my_checks card: a worker's own ID check (a passport or ID card photo
// plus a selfie) and Right to Work check (a share code and date of birth).
// The server says where they stand; the photos and the code are sent from
// the card with the page's own calls, as Profile → ID & RTW sends them.

import { SHARE_CODE_PROBLEM, validShareCode } from '../../../../utils/shareCode';

export type IdCheckStatus = 'verified' | 'checking' | 'needed' | 'declined' | 'expired';
export type RtwCheckStatus = 'verified' | 'needed' | 'no_rtw';
// What the card did: sending, then started (the check runs in the background)
// or failed with a message to show.
export type CheckSendState = 'sending' | 'started' | 'failed';

export interface MyChecksData {
  focus: 'id' | 'selfie' | 'rtw' | null;
  id: {
    status: IdCheckStatus;
    expires: string;
    hasDocument: boolean;
    hasSelfie: boolean;
    state?: CheckSendState;
    message?: string;
  };
  rtw: {
    status: RtwCheckStatus;
    expires: string;
    // False for British and Irish citizens: they have no share code.
    applies: boolean;
    shareCode: string;
    // YYYY-MM-DD, or '' when the profile has none.
    dateOfBirth: string;
    state?: CheckSendState;
    message?: string;
  };
}

// Shared with Profile → ID & RTW; re-exported so the card keeps one import.
export {
  formatShareCode,
  normaliseShareCode,
  rtwErrorMessage,
  validShareCode,
} from '../../../../utils/shareCode';

export const GET_SHARE_CODE_URL = 'https://www.gov.uk/prove-right-to-work/get-a-share-code-online';

// A date input gives YYYY-MM-DD. Someone working is between 14 and 100.
export const validBirthDate = (iso: string, now = new Date()) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) {
    return false;
  }
  const age = now.getUTCFullYear() - Number(match[1]);
  return age >= 14 && age <= 100;
};

export const idDone = (data: MyChecksData) => data.id.status === 'verified';
export const rtwDone = (data: MyChecksData) => !data.rtw.applies || data.rtw.status === 'verified';

// Sent and waiting on the check, so nothing to do here for now.
export const idWaiting = (data: MyChecksData) =>
  data.id.status === 'checking' || data.id.state === 'started';
export const rtwWaiting = (data: MyChecksData) => data.rtw.state === 'started';

// What still needs the worker: 0, 1 or 2.
export const checksToDo = (data: MyChecksData) =>
  [!idDone(data) && !idWaiting(data), !rtwDone(data) && !rtwWaiting(data)].filter(Boolean).length;

// The ID check needs both photos, new or on file. Photos that failed or
// expired need at least one new one: the same pair would fail again.
export const canSendId = (data: MyChecksData, photos: { document?: File; selfie?: File }) => {
  const document = !!photos.document || data.id.hasDocument;
  const selfie = !!photos.selfie || data.id.hasSelfie;
  const somethingNew = !!photos.document || !!photos.selfie;
  const mustBeNew = data.id.status === 'declined' || data.id.status === 'expired';
  return document && selfie && (somethingNew || !mustBeNew) && data.id.state !== 'sending';
};

export const rtwProblem = (shareCode: string, dateOfBirth: string): string | null => {
  if (!validShareCode(shareCode)) {
    return SHARE_CODE_PROBLEM;
  }
  if (!validBirthDate(dateOfBirth)) {
    return 'Enter your date of birth';
  }
  return null;
};
